import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { getColorEntities } from 'common/entities/color/selectors';
import { getFontEntities } from 'common/entities/font/selectors';
import { getStyleConfiguration } from 'common/entities/form/selectors';
import {
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_TEXT,
} from 'common/constants/brandLibrary';

import {
    DEFAULT_FONT,
    DEFAULT_FONT_SIZE,
    DEFAULT_ERRORS_COLOR,
    DEFAULT_INPUT_LABEL_COLOR,
    DEFAULT_INPUT_ANSWER_COLOR,
    DEFAULT_INPUT_HELPER_TEXT_COLOR,
    DEFAULT_INPUT_PLACEHOLDER_COLOR,
    DEFAULT_INPUT_BORDER_COLOR,
    DEFAULT_INPUT_BACKGROUND_COLOR,
    DEFAULT_INPUT_BORDER_COLOR_HOVER,
    DEFAULT_INPUT_BORDER_COLOR_FOCUS,
    DEFAULT_INPUT_BORDER_RADIUS,
    DEFAULT_INPUT_BORDER_WIDTH,
    DEFAULT_BUTTON_LABEL_COLOR,
    DEFAULT_BUTTON_BACKGROUND_COLOR,
    DEFAULT_BUTTON_BACKGROUND_COLOR_HOVER,
    DEFAULT_BUTTON_BORDER_COLOR,
    DEFAULT_BUTTON_BORDER_COLOR_HOVER,
    DEFAULT_BUTTON_BORDER_COLOR_FOCUS,
    DEFAULT_BUTTON_BORDER_WIDTH,
    DEFAULT_BUTTON_BORDER_RADIUS,
    DEFAULT_BUTTON_FONT_WEIGTH,
    DEFAULT_SPACING,
    DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND,
    DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR,
    DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR_HOVER,
    DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR,
} from 'common/entities/form/styleConfiguration/defaultValues';
import {
    SPACING_STANDARD,
    SPACING_CUSTOM,
    SPACING_DATA_NODES_SEPARATION,
    SPACING_DATA_LABEL_SEPARATION,
} from 'common/entities/form/styleConfiguration/constants';

import SuiThemeProvider from '@splashthat/sui/lib/components/SuiThemeProvider';
import { createSuiTheme } from '@splashthat/sui/lib/theme';

import { withFormId } from '../components/FormIdContext';

import parseFontSize from '../utils/parseFontSize';
import transformValueToPixels from '../utils/transformValueToPixels';

const calculations = {
    [SPACING_CUSTOM]: (mode, labelSeparation, nodesSeparation) => ({
        questionMarginBottom: `${nodesSeparation / 16.0}rem`,
        marginLabel: `${labelSeparation / 16.0}rem`,
    }),
    default: (mode, labelSeparation, nodesSeparation) => ({
        questionMarginBottom: `${DEFAULT_SPACING[mode][SPACING_DATA_NODES_SEPARATION] / 16.0}em`,
        marginLabel: `${DEFAULT_SPACING[mode][SPACING_DATA_LABEL_SEPARATION] / 16.0}em`,
    }),
};
const calculateSpacing = (mode, labelSeparation, nodesSeparation) =>
    (calculations[mode] || calculations.default)(mode, labelSeparation, nodesSeparation);

/**
 * Wraps form renderer and adds a custom theme provider over all Sui components inside.
 */
const FormThemeWrapper = ({ children, styleConfiguration, colorEntities, fontEntities }) => {
    /* eslint-disable max-len */
    const getColorValue = (id, fallback) => get(colorEntities, [id, 'value'], fallback);
    const getFontFamilyValue = (id, fallback) => get(fontEntities, [id, 'value'], fallback);

    const spacingMode = get(styleConfiguration, ['form', 'spacing', 'mode'], SPACING_STANDARD);
    const spacingLabelSeparation = get(
        styleConfiguration,
        ['form', 'spacing', 'label'],
        DEFAULT_SPACING[SPACING_STANDARD][SPACING_DATA_LABEL_SEPARATION],
    );
    const spacingNodesSeparation = get(
        styleConfiguration,
        ['form', 'spacing', 'question'],
        DEFAULT_SPACING[SPACING_STANDARD][SPACING_DATA_NODES_SEPARATION],
    );
    const { questionMarginBottom, marginLabel } = calculateSpacing(
        spacingMode,
        spacingLabelSeparation,
        spacingNodesSeparation,
    );

    const customizedThemeProps = {
        components: {
            suiButton: {
                variants: {
                    base: {
                        font: {
                            color: getColorValue(
                                get(styleConfiguration, ['button', 'text', 'colors', 'default']),
                                DEFAULT_BUTTON_LABEL_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['button', 'text', 'colors', 'hover']),
                                    DEFAULT_BUTTON_LABEL_COLOR,
                                ),
                            },
                            family: getFontFamilyValue(
                                get(styleConfiguration, ['button', 'text', 'font']),
                                DEFAULT_FONT,
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['button', 'text', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                            weight: DEFAULT_BUTTON_FONT_WEIGTH,
                        },
                        background: {
                            color: getColorValue(
                                get(styleConfiguration, ['button', 'colors', 'default']),
                                DEFAULT_BUTTON_BACKGROUND_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['button', 'colors', 'hover']),
                                    DEFAULT_BUTTON_BACKGROUND_COLOR_HOVER,
                                ),
                            },
                            active: {
                                color: getColorValue(
                                    get(styleConfiguration, ['button', 'colors', 'hover']),
                                    DEFAULT_BUTTON_BACKGROUND_COLOR_HOVER,
                                ),
                            },
                        },
                        border: {
                            color: getColorValue(
                                get(styleConfiguration, ['button', 'border', 'colors', 'default']),
                                DEFAULT_BUTTON_BORDER_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['button', 'border', 'colors', 'hover']),
                                    DEFAULT_BUTTON_BORDER_COLOR_HOVER,
                                ),
                            },
                            focus: {
                                color: getColorValue(
                                    get(styleConfiguration, ['button', 'border', 'colors', 'focus']),
                                    DEFAULT_BUTTON_BORDER_COLOR_FOCUS,
                                ),
                            },
                            radius: transformValueToPixels(
                                get(styleConfiguration, ['button', 'border', 'radius'], DEFAULT_BUTTON_BORDER_RADIUS),
                            ),
                            top: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['button', 'border', 'sizes', 'top'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                            right: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['button', 'border', 'sizes', 'right'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                            bottom: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['button', 'border', 'sizes', 'bottom'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                            left: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['button', 'border', 'sizes', 'left'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                        },
                    },
                    secondary: {
                        font: {
                            color: getColorValue(
                                get(styleConfiguration, ['secondaryButton', 'text', 'colors', 'default']),
                                get(
                                    colorEntities,
                                    [BRAND_LIBRARY_DARK_TEXT_COLOR, 'value'],
                                    DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR,
                                ),
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['secondaryButton', 'text', 'colors', 'hover']),
                                    get(
                                        colorEntities,
                                        [BRAND_LIBRARY_LIGHT_TEXT_COLOR, 'value'],
                                        DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR_HOVER,
                                    ),
                                ),
                            },
                            family: getFontFamilyValue(
                                get(styleConfiguration, ['secondaryButton', 'text', 'font']),
                                get(fontEntities, [BRAND_LIBRARY_BUTTON_TEXT, 'value'], DEFAULT_FONT),
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['secondaryButton', 'text', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                        },
                        background: {
                            color: getColorValue(
                                get(styleConfiguration, ['secondaryButton', 'colors', 'default']),
                                get(
                                    colorEntities,
                                    [BRAND_LIBRARY_LIGHT_TEXT_COLOR, 'value'],
                                    DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND,
                                ),
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['secondaryButton', 'colors', 'hover']),
                                    get(
                                        colorEntities,
                                        [BRAND_LIBRARY_DARK_TEXT_COLOR, 'value'],
                                        DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND,
                                    ),
                                ),
                            },
                            active: {
                                color: getColorValue(
                                    get(styleConfiguration, ['secondaryButton', 'colors', 'hover']),
                                    get(
                                        colorEntities,
                                        [BRAND_LIBRARY_DARK_TEXT_COLOR, 'value'],
                                        DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND,
                                    ),
                                ),
                            },
                        },
                        border: {
                            color: getColorValue(
                                get(styleConfiguration, ['secondaryButton', 'border', 'colors', 'default']),
                                get(
                                    colorEntities,
                                    [BRAND_LIBRARY_DARK_TEXT_COLOR, 'value'],
                                    DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR,
                                ),
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['secondaryButton', 'border', 'colors', 'hover']),
                                    get(
                                        colorEntities,
                                        [BRAND_LIBRARY_DARK_TEXT_COLOR, 'value'],
                                        DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR,
                                    ),
                                ),
                            },
                            focus: {
                                color: getColorValue(
                                    get(styleConfiguration, ['secondaryButton', 'border', 'colors', 'focus']),
                                    get(
                                        colorEntities,
                                        [BRAND_LIBRARY_DARK_TEXT_COLOR, 'value'],
                                        DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR,
                                    ),
                                ),
                            },
                            radius: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['secondaryButton', 'border', 'radius'],
                                    DEFAULT_BUTTON_BORDER_RADIUS,
                                ),
                            ),
                            top: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['secondaryButton', 'border', 'sizes', 'top'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                            right: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['secondaryButton', 'border', 'sizes', 'right'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                            bottom: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['secondaryButton', 'border', 'sizes', 'bottom'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                            left: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['secondaryButton', 'border', 'sizes', 'left'],
                                    DEFAULT_BUTTON_BORDER_WIDTH,
                                ),
                            ),
                        },
                    },
                },
            },
            suiField: {
                variants: {
                    base: {
                        font: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'inputText', 'colors', 'answer']),
                                DEFAULT_INPUT_ANSWER_COLOR,
                            ),
                            placeholderColor: getColorValue(
                                get(styleConfiguration, ['formFields', 'inputText', 'colors', 'placeholder']),
                                DEFAULT_INPUT_PLACEHOLDER_COLOR,
                            ),
                            family: getFontFamilyValue(
                                get(styleConfiguration, ['formFields', 'inputText', 'font']),
                                DEFAULT_FONT,
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['formFields', 'inputText', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                        },
                        background: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'inputBackgroundColors', 'default']),
                                DEFAULT_INPUT_BACKGROUND_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'inputBackgroundColors', 'hover']),
                                    DEFAULT_INPUT_BACKGROUND_COLOR,
                                ),
                            },
                        },
                        border: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'border', 'colors', 'default']),
                                DEFAULT_INPUT_BORDER_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'border', 'colors', 'hover']),
                                    DEFAULT_INPUT_BORDER_COLOR_HOVER,
                                ),
                            },
                            focus: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'border', 'colors', 'focus']),
                                    DEFAULT_INPUT_BORDER_COLOR_FOCUS,
                                ),
                            },
                            radius: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['formFields', 'border', 'radius'],
                                    DEFAULT_INPUT_BORDER_RADIUS,
                                ),
                            ),
                            top: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['formFields', 'border', 'sizes', 'top'],
                                    DEFAULT_INPUT_BORDER_WIDTH,
                                ),
                            ),
                            bottom: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['formFields', 'border', 'sizes', 'bottom'],
                                    DEFAULT_INPUT_BORDER_WIDTH,
                                ),
                            ),
                            right: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['formFields', 'border', 'sizes', 'right'],
                                    DEFAULT_INPUT_BORDER_WIDTH,
                                ),
                            ),
                            left: transformValueToPixels(
                                get(
                                    styleConfiguration,
                                    ['formFields', 'border', 'sizes', 'left'],
                                    DEFAULT_INPUT_BORDER_WIDTH,
                                ),
                            ),
                        },
                    },
                },
            },
            suiLabel: {
                variants: {
                    base: {
                        font: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'questionText', 'color']),
                                DEFAULT_INPUT_LABEL_COLOR,
                            ),
                            colorError: getColorValue(
                                get(styleConfiguration, ['errors', 'color']),
                                DEFAULT_ERRORS_COLOR,
                            ),
                            family: getFontFamilyValue(
                                get(styleConfiguration, ['formFields', 'questionText', 'font']),
                                DEFAULT_FONT,
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['formFields', 'questionText', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                            weight: 400,
                        },
                        margin: `0 0 ${marginLabel} 0`,
                        padding: '0',
                    },
                },
            },
            suiHelperText: {
                variants: {
                    base: {
                        font: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'helperText', 'color']),
                                DEFAULT_INPUT_HELPER_TEXT_COLOR,
                            ),
                            family: getFontFamilyValue(
                                get(styleConfiguration, ['formFields', 'helperText', 'font']),
                                DEFAULT_FONT,
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['formFields', 'helperText', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                        },
                    },
                },
            },
            suiCheckbox: {
                variants: {
                    base: {
                        background: {
                            colorChecked: getColorValue(
                                get(styleConfiguration, ['formFields', 'inputText', 'colors', 'answer']),
                                DEFAULT_INPUT_ANSWER_COLOR,
                            ),
                        },
                        font: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'questionText', 'color']),
                                DEFAULT_INPUT_LABEL_COLOR,
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['formFields', 'questionText', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                            family: getFontFamilyValue(
                                get(styleConfiguration, ['formFields', 'questionText', 'font']),
                                DEFAULT_FONT,
                            ),
                        },
                        border: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'border', 'colors', 'default']),
                                DEFAULT_INPUT_BORDER_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'border', 'colors', 'hover']),
                                    DEFAULT_INPUT_BORDER_COLOR_HOVER,
                                ),
                            },
                            focus: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'border', 'colors', 'hover']),
                                    DEFAULT_INPUT_BORDER_COLOR_HOVER,
                                ),
                            },
                        },
                        indicator: {
                            size: parseFontSize(
                                get(styleConfiguration, ['formFields', 'inputText', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                        },
                    },
                },
            },
            suiRadioIndicator: {
                variants: {
                    base: {
                        background: {
                            colorChecked: getColorValue(
                                get(styleConfiguration, ['formFields', 'inputText', 'colors', 'answer']),
                                DEFAULT_INPUT_ANSWER_COLOR,
                            ),
                        },
                        border: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'border', 'colors', 'default']),
                                DEFAULT_INPUT_BORDER_COLOR,
                            ),
                            hover: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'border', 'colors', 'hover']),
                                    DEFAULT_INPUT_BORDER_COLOR_HOVER,
                                ),
                            },
                            focus: {
                                color: getColorValue(
                                    get(styleConfiguration, ['formFields', 'border', 'colors', 'hover']),
                                    DEFAULT_INPUT_BORDER_COLOR_HOVER,
                                ),
                            },
                        },
                        indicator: {
                            color: getColorValue(
                                get(styleConfiguration, ['formFields', 'inputText', 'colors', 'answer']),
                                DEFAULT_INPUT_ANSWER_COLOR,
                            ),
                            size: parseFontSize(
                                get(styleConfiguration, ['formFields', 'inputText', 'fontSize'], DEFAULT_FONT_SIZE),
                            ),
                        },
                    },
                },
            },
        },
    };

    // FIXME we are mixing SUI theme with splash_web-only theme properties to make Form
    //  builder work (concretely the Form Spacing style option). This should either map to a SUI
    //  component that can be themed via createSuiTheme or both parts (SUI and Form Builder)
    //  should have separated theming solutions.
    const formBuilderTheme = {
        ...createSuiTheme(customizedThemeProps),
        questionMarginBottom,
    };

    return <SuiThemeProvider theme={formBuilderTheme}>{children}</SuiThemeProvider>;
    /* eslint-enable max-len */
};

FormThemeWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    styleConfiguration: PropTypes.object.isRequired,
    colorEntities: PropTypes.object.isRequired,
    fontEntities: PropTypes.object.isRequired,
};

// Exported for unit testing.
export const mapStateToProps = (state, props) => {
    const colorEntities = getColorEntities(state);
    const fontEntities = getFontEntities(state);
    const styleConfiguration = getStyleConfiguration(state, props.formId);
    return {
        colorEntities,
        fontEntities,
        styleConfiguration,
    };
};

export default withFormId(connect(mapStateToProps)(FormThemeWrapper));
